/**
 * Notice — 화면 안에 눌러앉는 안내 한 칸.
 *
 * Astryx `Banner` 를 대신한다 (2026-08-28 검수). Banner 가 이 앱에서 낸 문제는 셋:
 *
 * ① **아이콘이 peeka 것이 아니다.** Banner 는 제 lucide 아이콘을 박는다
 *    (실측: `class="lucide lucide-info"`). 앱의 나머지는 전부 픽셀 아이콘이라
 *    이 한 칸만 다른 세상에서 온 그림이었다.
 * ② **다크 모드에 밝은 하늘색 판이 떴다.** y2k 는 상태색 배경을 라이트·다크
 *    같은 값(#B8E0FF)으로 둔다. 어두운 화면 한가운데 흰 판이 하나 서 있었다.
 * ③ **그 판을 고치려던 global.css 규칙이 안의 버튼을 깨뜨렸다.** 배너 안에서
 *    --text-primary 를 어두운 파랑으로 되돌렸는데, 그 안의 secondary 버튼은
 *    제 배경(어두운 표면)을 갖고 있어서 **어두운 배경에 어두운 글자**가 됐다 —
 *    실측 대비 **1.22:1**. 본문은 10.32:1 로 멀쩡한데 버튼만 안 읽혔다.
 *
 * 그래서 상태색 판을 쓰지 않는다. **표면 + 테두리 + 픽셀 아이콘**이고, 상태는
 * **아이콘의 모양과 색**만 바꾼다. 글자는 앱의 보통 토큰을 그대로 쓰므로 대비가
 * 저절로 맞는다 — 고칠 것이 없으면 깨질 것도 없다.
 *
 * ⚠ 토스트와 다르다. 토스트는 **방금 한 일**의 결과를 잠깐 알리고 사라지지만,
 *   이건 화면에 남아 **지금 이 자리의 사정**을 말한다 (시세가 없다 · 위젯을
 *   더할 수 있다). 사라지는 것과 남는 것을 섞지 말 것.
 */
import type { ReactNode } from "react";
import { Text } from "@astryxdesign/core/Text";
import { VStack } from "@astryxdesign/core/Layout";
import Icon, { type IconName } from "../primitives/Icon";
import styles from "./Notice.module.css";

export type NoticeStatus = "info" | "warning" | "error";

const STATUS_ICON: Record<NoticeStatus, IconName> = {
  info: "Info",
  /* 경고·오류는 같은 그림(경고 삼각형)에 색만 다르다 — 둘의 차이는 '주의해서
     보라' 와 '잘못됐다' 이고, 그건 색이 말하는 편이 빠르다 */
  warning: "Report",
  error: "Report",
};

export interface NoticeProps {
  status?: NoticeStatus;
  title: string;
  description?: string;
  /** 오른쪽에 붙는 액션 (버튼 등) */
  action?: ReactNode;
  className?: string;
}

export default function Notice({
  status = "info",
  title,
  description,
  action,
  className,
}: NoticeProps) {
  return (
    <div
      className={`${styles.notice} ${styles[status]} ${className ?? ""}`}
      role="status"
    >
      {/* 아이콘은 글의 **첫 줄에 맞춘다**(align start). 가운데로 맞추면 설명이
          길어질수록 아이콘이 아래로 내려가 제목과 따로 논다 */}
      <span className={styles.icon} aria-hidden="true">
        <Icon name={STATUS_ICON[status]} size="sm" />
      </span>

      <VStack gap={0.5} align="start">
        <Text weight="semibold">{title}</Text>
        {description && (
          <Text type="supporting" color="secondary">{description}</Text>
        )}
      </VStack>

      {/* 액션은 **아래**가 아니라 오른쪽이다 — 안내가 두 줄이면 아래에 둘 때
          한 칸이 통째로 세 줄이 되어 화면을 먹는다.
          ⚠ 좁은 화면에서는 CSS 가 아래로 내린다(styles.notice 참조). */}
      {action && <div className={styles.action}>{action}</div>}
    </div>
  );
}
